import React, { useEffect, useMemo, useState } from "react";
// ANT DESIGN COMPONENTS
import { Spin, Divider, Button, Input, Select } from "antd";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faList,
  faShieldHalved,
  faTableCells,
  faUserShield,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useDispatch, useSelector } from "react-redux";
import {
  usePostRoleMutation,
  usePutRoleMutation,
  useLazyGetRolesQuery,
  useLazyGetRoleQuery,
} from "../../../redux/roles/roleAPI";
import { useGetOrganizationsLightQuery } from "../../../redux/organizations/organizationAPI";
import { updateOrganizationAndChilds } from "../../../redux/organizations/organizationSlice";
// COMPONENTS
import RolesPermissionsCards from "./RolesPermissionsCards";
import RolesSidebarSection from "./RolesSidebarSection";
import GlobalAlert2 from "../../home/GlobalAlert2";
import { withMissingPermissions } from "./roleSaveError";
import FormInputText from "../../components/form/FormInputText";
// AUTHORIZATION
import GETJwtToken from "../../../redux/authentication/authentication";
// PERMISSION CATALOGUE — fallback ceiling and the permission descriptions
import useGetPermissionCatalogue from "../../../customHooks/GET_requests/useGetPermissionCatalogue";
// COMPONENTS
import OrganizationSelectOrganizationSubOrganization from "../organizations/OrganizationSelectOrganizationSubOrganization";
// INTERFACES
import { FormularyType } from "../../../interfaces/Wizard.interface";
import { FormAction } from "../../../types/enums";
// FLATBUFFER DISPLAY
import { formatUuidT } from "../../../flatbuffers/display";
// SCOPE — edit mode preserves the role's existing bank/lift/floor grants
// unmodified (no UI for them here yet); omitting them from the PUT body
// would risk the backend wiping them on save.
import { extractRoleScopeGrants } from "../../../redux/scope/extractRoleScopeGrants";
import { grantsToBodyArrays } from "../../../redux/scope/scopeSerialization";
// ROLE MENU (FlatBuffer Object API — same model the wizard editor + Edit form use)
import {
  roleMenuSaveJson,
  readRolePermissionGrants,
  safeParseRoleMenu,
  editorCeilingMenu,
  applyCheckedKeys,
  pruneGrantedMenu,
  roleMenuTreeData,
  collectMenuDescriptions,
  readMenuDescription,
  collectLockedActionTreeKeys,
} from "../../setupWizard/utils/roleMenu";
import type { RoleMenuT } from "../../../flatbuffers/generated/archive/role-menu";
// MASTER ADMIN GUARD — the "Role Base" template picker below is master-admin only
import { isMasterAdmin } from "../../../utils/isMasterAdmin";
// MASTER-ORG ROLE MARKER — same ownership check + star the wizard's role
// lists use, so a LiftNet role is recognisable in this picker too.
import { isMasterOrgRole } from "../../../utils/masterOrganization";
import MasterRoleStar from "../../../components/roles/MasterRoleStar";
// TENANT ROLE MARKER — the counterpart to MasterRoleStar: every option that
// is NOT a LiftNet/MASTER role gets this shield instead, so every row in the
// picker carries one marker or the other.
import TenantRoleShield from "../../../components/roles/TenantRoleShield";
// INTERFACES
import { RootState } from "../../../app/store";

interface RolesCreateRoleFormProps {
  mode?: FormAction;
  roleId?: string;
  initialRole?: any;
  onCancel?: () => void;
  onSaveSuccess?: () => void;
}

function RolesCreateRoleForm({
  mode = FormAction.Create,
  roleId = "",
  initialRole = null,
  onCancel = () => {},
  onSaveSuccess = () => {},
}: RolesCreateRoleFormProps) {
  // ************************************************ */
  // LOCAL STORAGE AND VARIABLES ******************** */
  const isEditMode = mode === FormAction.Edit;
  // Read-only rendering of an existing role: same layout as edit, but every
  // input disabled and no save. Reached from the Roles table's "View" action
  // on a master-organization role — a tenant user may look at what that role
  // grants, never change it.
  const isViewMode = mode === FormAction.View;
  // Both edit and view are bound to the role's CURRENT organization — neither
  // may move it — so both render the locked Organization display instead of
  // create mode's cascading Organization / Sub Organization selector.
  const hasFixedOrganization = isEditMode || isViewMode;
  // The "Role Base" template picker exists ONLY on the create route
  // (/organization-management/roles/create). Editing a role must never
  // re-seed its permissions from another role, so this stays false for both
  // edit and view.
  const isCreateMode = !isEditMode && !isViewMode;
  const dispatch = useDispatch();
  const { organizationAndChilds } = useSelector((state: any) => state.organization);
  const { userLogged, globalOrganizationId } = useSelector((state: any) => state.home);
  // The raw permission menu as delivered at login — one of the sources the
  // per-permission descriptions are read from (see permissionDescriptions).
  const { permissions } = useSelector((state: any) => state.user);
  const [organizationsAndChildsIsLoading, setOrganizationsAndChildsIsLoading] = useState(false);
  const [createMenu, setCreateMenu] = useState<RoleMenuT | null>(null);
  const [t] = useTranslation("global");
  const [roleName, setRoleName] = useState("");
  // The role menu's OWN description (RoleMenuT.description, e.g.
  // { name, pages, active, offline, description }) — kept as its own piece
  // of form state, same pattern as roleName, and stamped onto menuObj right
  // before save rather than living on `createMenu` itself: createMenu is
  // rebuilt via applyCheckedKeys on every single permission toggle, which
  // only carries name/active/offline/pages through, so anything smuggled
  // onto the live instance would be wiped by the next switch click.
  const [description, setDescription] = useState("");
  // Token for the light-orgs lookup that resolves the disabled Organization
  // field's display name in edit mode (initialRole only carries the org id).
  const [tokenOrganizations, setTokenOrganizations] = useState<string | null>(null);
  // ROLE BASE (create mode + master admin only) — id of the role whose menu is
  // currently being used as the starting copy, plus the list it is chosen from.
  const [roleBaseId, setRoleBaseId] = useState<string>("");
  const [roleBaseList, setRoleBaseList] = useState<any[]>([]);
  const [isLoadingRoleBase, setIsLoadingRoleBase] = useState(false);
  // Only a master admin may copy another role as a template: every other user
  // is confined to the blank-start flow bounded by their own menu.
  const canSelectRoleBase = isCreateMode && isMasterAdmin(userLogged, globalOrganizationId);
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */
  const [
    postRole,
    {
      isSuccess: isSuccessPostRole,
      isLoading: isLoadingPostRole,
      isError: isErrorPostRole,
      error: errorPostRole,
      reset: resetPostRole,
    },
  ] = usePostRoleMutation();

  const [
    putRole,
    {
      isSuccess: isSuccessPutRole,
      isLoading: isLoadingPutRole,
      isError: isErrorPutRole,
      error: errorPutRole,
      reset: resetPutRole,
    },
  ] = usePutRoleMutation();

  const [triggerGetRoles] = useLazyGetRolesQuery();
  const [triggerGetRole] = useLazyGetRoleQuery();
  const { catalogueMenu, isLoadingPermissionCatalogue } = useGetPermissionCatalogue();

  const { data: dataOrganizationsLight } = useGetOrganizationsLightQuery(
    { token: tokenOrganizations },
    {
      skip: !tokenOrganizations || !hasFixedOrganization,
      refetchOnMountOrArgChange: false,
    },
  );

  const isLoadingSave = isLoadingPostRole || isLoadingPutRole;
  const isSuccessSave = isSuccessPostRole || isSuccessPutRole;
  const isErrorSave = isErrorPostRole || isErrorPutRole;
  const errorSave = errorPostRole || errorPutRole;

  // Edit/view-mode org id/name — the role is locked to its current
  // organization (see the disabled Organization field below), so this never
  // reads from organizationAndChilds like create mode does.
  const editOrgId = formatUuidT(initialRole?.orgId ?? initialRole?.org_id);
  const organizationNameById = useMemo(() => {
    const result: Record<string, string> = {};
    (dataOrganizationsLight?.data ?? []).forEach((organization: any) => {
      const id = formatUuidT(organization?.id);
      const name = String(organization?.name ?? "").trim();
      if (id && name) result[id] = name;
    });
    return result;
  }, [dataOrganizationsLight]);
  const editOrganizationDisplayName =
    organizationNameById[editOrgId] ||
    String(initialRole?.orgName ?? initialRole?.org?.name ?? "").trim() ||
    editOrgId;

  // ************************************************ */
  // FUNCTIONS ************************************** */
  const cleanOrganizationsList = () => {
    dispatch(
      updateOrganizationAndChilds({
        childs: [],
      }),
    );
  };

  // A newly-created role starts with NO permissions granted. Seed the editable
  // tree from the creator's own menu — you can only grant permissions you hold —
  // then clear every flag so the operator starts from a blank grant and checks
  // only what the new role should have. applyCheckedKeys with an empty set
  // unchecks all actions for a mode and derives every page/sub-page flag to
  // false, keeping the full page/action structure while zeroing the grants.
  // The one exception is a LOCKED permission (action.locked, or inherited from
  // a locked page/sub_page): its switch can never be unchecked in the cards
  // editor, so it must start GRANTED rather than blank — checkedKeys seeds
  // with every locked action key instead of an empty set.
  // The creator's menu arrives as the whole catalogue with their effective
  // flags, so editorCeilingMenu prunes what they do not hold; it falls back to
  // the server's permission catalogue when the creator's menu is absent.
  const buildEmptyCreateMenu = (): RoleMenuT | null => {
    const base = editorCeilingMenu(userLogged?.role?.menu, catalogueMenu);
    if (!base) return null;
    const lockedKeys = collectLockedActionTreeKeys(base);
    return applyCheckedKeys(applyCheckedKeys(base, lockedKeys, "online"), lockedKeys, "offline");
  };

  // Build the editable tree from ANOTHER role used as a template.
  //
  // The ceiling is STILL the logged user's own menu — exactly as the blank
  // create path above — because you can never grant a permission you do not
  // hold, no matter which role was picked as the base. Only the CHECKED state
  // comes from the selected role: its granted keys are overlaid onto that
  // ceiling, so the operator opens on an exact copy of that role's grants and
  // may then add or remove from it.
  //
  // A key the base role holds but the logged user does not simply does not
  // exist in the ceiling tree, so applyCheckedKeys ignores it — the copy is
  // clipped to what the creator may actually delegate.
  const buildMenuFromBaseRole = (baseRole: any): RoleMenuT | null => {
    const ceiling = editorCeilingMenu(userLogged?.role?.menu, catalogueMenu);
    if (!ceiling) return null;
    const baseMenu = safeParseRoleMenu(baseRole?.menu ?? baseRole?.menuJson ?? baseRole?.menu_json);
    if (!baseMenu) {
      const lockedKeys = collectLockedActionTreeKeys(ceiling);
      return applyCheckedKeys(
        applyCheckedKeys(ceiling, lockedKeys, "online"),
        lockedKeys,
        "offline",
      );
    }
    // Locked ceiling keys are unioned in on top of the base role's own grants
    // — same "can never be unchecked" rule as the blank-start path above, so
    // picking a base role can never leave a locked/mandatory permission off.
    const lockedKeys = collectLockedActionTreeKeys(ceiling);
    const onlineChecked = new Set([
      ...roleMenuTreeData(baseMenu, "online").checkedKeys,
      ...lockedKeys,
    ]);
    const offlineChecked = new Set([
      ...roleMenuTreeData(baseMenu, "offline").checkedKeys,
      ...lockedKeys,
    ]);
    return applyCheckedKeys(
      applyCheckedKeys(ceiling, Array.from(onlineChecked), "online"),
      Array.from(offlineChecked),
      "offline",
    );
  };

  // Options for the Role Base picker. `name` is kept as a plain string
  // alongside the rendered label because the label carries the master-org star
  // (a React node) and antd can only substring-search a string — filtering on
  // the node would never match anything the operator types.
  const roleBaseOptions = useMemo(
    () =>
      roleBaseList
        .map((role: any) => ({
          id: formatUuidT(role?.id),
          name: String(role?.displayName ?? role?.name ?? "").trim() || formatUuidT(role?.id),
          isMaster: isMasterOrgRole(role),
        }))
        .filter((option) => option.id)
        // LiftNet/MASTER roles surface first — Array#sort is stable, so within
        // each group (master / non-master) the original list order is kept.
        .sort((a, b) => Number(b.isMaster) - Number(a.isMaster)),
    [roleBaseList],
  );

  // Selecting a base role re-seeds the permission cards from that role; clearing
  // the field puts the form back to the blank create tree. The role is re-fetched
  // by id because the paginated /roles/ list is not guaranteed to carry the full
  // menu payload — the list row is only a fallback if that lookup fails.
  const onChangeRoleBase = async (value: string | undefined) => {
    if (!value) {
      setRoleBaseId("");
      setCreateMenu(buildEmptyCreateMenu());
      return;
    }
    setRoleBaseId(value);
    setIsLoadingRoleBase(true);
    const fromList = roleBaseList.find((role: any) => formatUuidT(role?.id) === value);
    try {
      const token = await GETJwtToken();
      const response = await triggerGetRole({ roleId: value, token }).unwrap();
      setCreateMenu(buildMenuFromBaseRole(response?.data ?? response ?? fromList));
    } catch {
      setCreateMenu(buildMenuFromBaseRole(fromList));
    } finally {
      setIsLoadingRoleBase(false);
    }
  };

  // Edit mode must NOT use the role-being-edited's own menu as the base
  // tree — that menu is typically already pruned down to just what the role
  // holds, so building the editor from it
  // makes every permission the editing user has but the role doesn't
  // invisible, and you can never grant more than the role already had. The
  // base/ceiling is always the editing user's own menu (same as Create) —
  // this just overlays the role's current grants onto that ceiling instead
  // of starting blank, so the editor opens showing what's already active.
  // Same menu source priority RolesEditRoleForm's drawer already uses.
  const buildEditMenu = (): RoleMenuT | null => {
    const base = editorCeilingMenu(userLogged?.role?.menu, catalogueMenu);
    if (!base) return null;
    const existingRoleMenu = safeParseRoleMenu(initialRole?.menu ?? initialRole?.menuJson);
    if (!existingRoleMenu) {
      return applyCheckedKeys(applyCheckedKeys(base, [], "online"), [], "offline");
    }
    const onlineChecked = roleMenuTreeData(existingRoleMenu, "online").checkedKeys;
    const offlineChecked = roleMenuTreeData(existingRoleMenu, "offline").checkedKeys;
    return applyCheckedKeys(
      applyCheckedKeys(base, onlineChecked, "online"),
      offlineChecked,
      "offline",
    );
  };

  // View mode shows ONLY what the role actually holds — the opposite of the
  // edit tree above. It reads the role's own menu (never the viewing user's
  // ceiling: a tenant user's ceiling has nothing to do with what a master-org
  // role grants) and prunes every fully un-granted action/page away, so the
  // cards list exactly the assigned permissions and nothing else. Returns
  // null when the role carries no parseable menu, which the cards render as
  // "no permissions" instead of as an empty editor.
  const buildViewMenu = (): RoleMenuT | null => {
    const existingRoleMenu = safeParseRoleMenu(initialRole?.menu ?? initialRole?.menuJson);
    if (!existingRoleMenu) return null;
    return pruneGrantedMenu(existingRoleMenu);
  };

  // Per-permission descriptions ride alongside the menu tree, NOT inside it.
  //
  // `createMenu` is a RoleMenuT, and MenuPage/MenuAction have no `description`
  // field in the FlatBuffer schema — so the field cannot be "added where the
  // object is created": menuJsonToRoleMenuT has nowhere to put it, and
  // applyCheckedKeys/pruneGrantedMenu rebuild every node through those same
  // constructors on each toggle, which would drop any property smuggled onto
  // an instance. Hence this parallel tree_key -> description lookup, read off
  // the RAW menu payloads and handed to the cards.
  //
  // EVERY candidate source is passed rather than a `?? ` chain that picks one:
  // the same menu reaches the app in several shapes and only some carry the
  // field. When /users/me is answered as FlatBuffer, `role.menu` arrives as an
  // already-unpacked RoleMenuT whose descriptions the wire format dropped,
  // while `roleMenu` / `menu_json` (the legacy JSON-encoded copy) still hold
  // them; when it is answered as JSON, `role.menu` has them directly. Merging
  // is safe — descriptions are global per tree_key, only their presence
  // differs per source, and a source that has none contributes nothing.
  //
  // `permissions` is the very menu Layout dispatched at login, i.e. the
  // create/edit ceiling; `initialRole`'s own menus cover view mode, which
  // renders the role's pruned menu and can name keys the viewer lacks.
  const permissionDescriptions = useMemo(
    () =>
      collectMenuDescriptions(
        catalogueMenu,
        permissions,
        userLogged?.roleMenu,
        userLogged?.role?.menu,
        userLogged?.role?.menuJson,
        userLogged?.role?.menu_json,
        initialRole?.menu,
        initialRole?.menuJson,
        initialRole?.menu_json,
      ),
    [catalogueMenu, permissions, userLogged, initialRole],
  );

  const onChangeRoleName = (newValue: string) => {
    setRoleName(newValue);
  };

  const onChangeDescription = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    setDescription(event.target.value);
  };

  const onClickSave = async () => {
    // Guard: never save from an unloaded menu (mirrors the Edit drawer).
    if (!createMenu) return;
    const token = await GETJwtToken();
    // Save the FULL edited tree, not a pruned copy, with an explicit decision
    // for every node the form shows: a switch left off is saved as a denial,
    // which LiftCloud's default rules cannot turn back on. On edit, the role's
    // existing decisions are the starting point, so a decision on a key this
    // form does not show (outside the editor's own ceiling) is kept.
    const menuObj = roleMenuSaveJson(
      createMenu,
      isEditMode ? readRolePermissionGrants(initialRole) : {},
    );
    // Stamp the chosen role name onto it (backends key off menu.name).
    menuObj.name = roleName;
    menuObj.description = description;

    if (isEditMode) {
      putRole({
        id: formatUuidT(initialRole?.id),
        token,
        body: {
          active: initialRole?.active ?? true,
          name: roleName,
          display_name: roleName,
          org_id: editOrgId,
          menu: menuObj,
          // Pass the role's existing scope grants through unchanged — this
          // view has no UI for them, so omitting them would risk clearing
          // them on save instead of leaving them alone.
          ...grantsToBodyArrays(extractRoleScopeGrants(initialRole)),
        },
      });
      return;
    }

    const body: Record<string, any> = {
      active: true,
      offline: true,
      menu: menuObj,
      name: roleName,
      org_id: organizationAndChilds.data.idOrganizationSelected
        ? organizationAndChilds.data.idOrganizationSelected
        : "",
    };

    postRole({ token, body });
  };

  const handleCloseForm = () => {
    cleanOrganizationsList();
    onCancel();
  };

  const disableSaveButton = () => roleName.trim() === "";

  // The role menu's OWN description (RoleMenuT.description, same object shape
  // as userLogged.role.menu). Shared between both Organization layouts below
  // so it always lands in the SAME container as Role name / Organization —
  // the fixed-org row's second row, or (create mode) below the cascading
  // Sub Organization select inside OrganizationSelectOrganizationSubOrganization.
  const descriptionField = (
    <div className="col-md-12 mt-3">
      <div className="generalStyles__inputFlexNameLarge">
        <span className="drawer__text">{t("organizationManagement.description")}</span>
      </div>
      <div className="generalStyles__width100" style={{ marginTop: "9px" }}>
        <Input.TextArea
          className="generalStyles__input"
          value={description}
          onChange={onChangeDescription}
          rows={3}
          disabled={isViewMode}
        />
      </div>
    </div>
  );

  // ************************************************* */
  // USE EFFECT ************************************** */
  // The dedicated create/edit route is always "open" — initialise once on
  // mount instead of reacting to a drawer's open flag.
  useEffect(() => {
    resetPostRole();
    resetPutRole();
    cleanOrganizationsList();
    if (isViewMode || isEditMode) {
      setRoleName(initialRole?.name ?? "");
      setDescription(readMenuDescription(initialRole?.menu ?? initialRole?.menuJson));
    } else {
      setRoleName("");
      setDescription("");
    }
  }, []);

  // The permission tree is built once, as soon as it can be: at once when the
  // editing user's own menu is at hand (view mode needs only the role's), and
  // otherwise when the catalogue fallback has loaded or failed to.
  const [menuInitialised, setMenuInitialised] = useState(false);
  useEffect(() => {
    if (menuInitialised) return;
    const ceilingReady =
      isViewMode || !!safeParseRoleMenu(userLogged?.role?.menu) || !isLoadingPermissionCatalogue;
    if (!ceilingReady) return;
    setMenuInitialised(true);
    if (isViewMode) {
      setCreateMenu(buildViewMenu());
    } else if (isEditMode) {
      setCreateMenu(buildEditMenu());
    } else {
      setCreateMenu(buildEmptyCreateMenu());
    }
  }, [menuInitialised, isLoadingPermissionCatalogue]);

  // Load the pickable base roles once, master-admin create route only.
  useEffect(() => {
    if (!canSelectRoleBase) return;
    (async () => {
      try {
        const token = await GETJwtToken();
        const response = await triggerGetRoles({
          page: 1,
          limit: 1000,
          token,
          searchAndFilterString: "",
        }).unwrap();
        setRoleBaseList(response?.data ?? []);
      } catch {
        setRoleBaseList([]);
      }
    })();
  }, [canSelectRoleBase]);

  useEffect(() => {
    if (hasFixedOrganization) {
      GETJwtToken().then(setTokenOrganizations);
    }
  }, [hasFixedOrganization]);

  useEffect(() => {
    if (isSuccessSave) {
      onSaveSuccess();
    }
  }, [isSuccessSave]);

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div>
      <Spin
        spinning={
          isLoadingSave || organizationsAndChildsIsLoading || isLoadingRoleBase || !menuInitialised
        }
        tip={t("general.loading")}
      >
        <div>
          <Divider orientation="left" className="generalStyles__drawerDivider">
            <h5>
              <FontAwesomeIcon
                icon={faUserShield}
                className="generalStyles__info generalStyles__mrFix"
              />
              {/* eslint-disable-next-line no-nested-ternary */}
              {isViewMode
                ? t("organizationManagement.viewRole")
                : isEditMode
                  ? t("organizationManagement.editRole")
                  : t("organizationManagement.createNewRole")}
            </h5>
          </Divider>
        </div>
        {/** ---------------------------------------------------- */}
        {/** ROLE NAME + GET ORGANIZATION AND CHILDS (same row, 50/50) ----------- */}
        {hasFixedOrganization ? (
          // Same drawer__box__{theme} background OrganizationSelectOrganizationSubOrganization
          // wraps its container in for create mode — kept here too so Edit/View
          // renders the identical card look instead of a bare, background-less block.
          <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
            <div className="container">
              <div className="row">
                <div className="col-md-6 mt-3">
                  <FormInputText
                    inputValue={roleName}
                    inputLabelName={t("organizationManagement.roleName")}
                    inputOnChange={onChangeRoleName}
                    inputVariableName="name"
                    isRequired
                    disabledInput={isViewMode}
                    formularyType={FormularyType.WIZARD}
                  />
                </div>
                {/** Organization is locked once a role belongs to the master
                    organization — it must never be moved to a different one,
                    so this is a disabled display, not the cascading
                    Organization/Sub Organization selector create mode uses. */}
                <div className="col-md-6 mt-3">
                  <div className="generalStyles__inputFlexNameLarge">
                    <span className="drawer__text">{t("organizationManagement.organization")}</span>
                  </div>
                  <div className="generalStyles__width100" style={{ marginTop: "9px" }}>
                    <Input
                      className="generalStyles__input"
                      value={editOrganizationDisplayName}
                      disabled
                      size="large"
                    />
                  </div>
                </div>
              </div>
              <div className="row">{descriptionField}</div>
            </div>
          </div>
        ) : (
          <OrganizationSelectOrganizationSubOrganization
            loadOnMount
            hideDivider
            organizationFieldColClassName="col-md-6 mt-3"
            setOrganizationsAndChildsIsLoading={setOrganizationsAndChildsIsLoading}
            extraFields={
              <div className="col-md-6 mt-3">
                <FormInputText
                  inputValue={roleName}
                  inputLabelName={t("organizationManagement.roleName")}
                  inputOnChange={onChangeRoleName}
                  inputVariableName="name"
                  isRequired
                  disabledInput={false}
                  formularyType={FormularyType.WIZARD}
                />
              </div>
            }
            extraFieldsBottom={descriptionField}
          />
        )}
        {/** ---------------------------------------------------- */}
        {/** ROLE BASE — copy another role's permissions as the starting point.
            Create route only, master admin only. */}

        {canSelectRoleBase && (
          <div>
            <div>
              <Divider orientation="left" className="generalStyles__drawerDivider">
                <h5>
                  <FontAwesomeIcon
                    icon={faList}
                    className="generalStyles__info generalStyles__mrFix"
                  />
                  {t("organizationManagement.roleBaseTitle")}
                </h5>
              </Divider>
            </div>
            <div className={`drawer__box__${theme}`} style={{ padding: "16px" }}>
              <div className="container">
                <div className="row">
                  <div className="col-md-12 mt-3">
                    <div className="generalStyles__inputFlexNameLarge">
                      <span className="drawer__text">{t("organizationManagement.roleBase")}</span>
                    </div>
                    <div className="generalStyles__width100" style={{ marginTop: "9px" }}>
                      <Select
                        showSearch
                        allowClear
                        size="large"
                        className="generalStyles__width100"
                        value={roleBaseId || undefined}
                        onChange={onChangeRoleBase}
                        filterOption={(input: string, option: any) =>
                          String(option?.label ?? "")
                            .toLowerCase()
                            .includes(input.toLowerCase())
                        }
                      >
                        {roleBaseOptions.map((option) => (
                          // `label` holds the searchable plain name; the children
                          // render the star/shield + name in both the list and
                          // the selected value. Every option gets exactly one
                          // marker: the master star for LiftNet/MASTER roles,
                          // the tenant shield for everything else.
                          <Select.Option key={option.id} value={option.id} label={option.name}>
                            <MasterRoleStar show={option.isMaster} />
                            <TenantRoleShield show={!option.isMaster} />
                            {option.name}
                          </Select.Option>
                        ))}
                      </Select>
                    </div>
                    <div className="mt-1">
                      <span className="drawer__text" style={{ fontSize: "12px", opacity: 0.7 }}>
                        {t("organizationManagement.roleBaseHelp")}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
        <br />
        {/** ---------------------------------------------------- */}
        {/** SIDEBAR — same tree Layout.tsx renders in the app's sidebar, one
            switch per tab, so hiding/showing a tab for this role doesn't
            require hunting for its row inside the full Permissions tree
            below. Toggling a row here edits the exact same menu tree_key. */}
        <div>
          <Divider orientation="left" className="generalStyles__drawerDivider">
            <h5>
              <FontAwesomeIcon
                icon={faTableCells}
                className="generalStyles__info generalStyles__mrFix"
              />
              {t("organizationManagement.sidebar") || "Sidebar"}
            </h5>
          </Divider>
          <span className="drawer__text" style={{ fontSize: "12px", opacity: 0.7 }}>
            {t("organizationManagement.sidebarHelp") ||
              "Show or hide which sidebar tabs this role can see."}
          </span>
        </div>
        <div>
          <RolesSidebarSection
            menu={createMenu}
            onChange={setCreateMenu}
            readOnly={isViewMode}
            descriptions={permissionDescriptions}
          />
        </div>
        <br />
        {/** ---------------------------------------------------- */}
        {/** PERMISSIONS */}
        <div>
          <Divider orientation="left" className="generalStyles__drawerDivider">
            <h5>
              <FontAwesomeIcon
                icon={faShieldHalved}
                className="generalStyles__info generalStyles__mrFix"
              />
              {t("general.permissions")}
            </h5>
          </Divider>
        </div>
        <div>
          <RolesPermissionsCards
            menu={createMenu}
            onChange={setCreateMenu}
            readOnly={isViewMode}
            descriptions={permissionDescriptions}
          />
        </div>
        {/** ---------------------------------------------------- */}
        <div className="container">
          <Button type="default" onClick={handleCloseForm} className="buttonStyle__3">
            {t("general.close")}
          </Button>
          {!isViewMode && (
            <Button
              onClick={onClickSave}
              disabled={isLoadingSave || disableSaveButton()}
              className="buttonStyle__3"
              type="primary"
            >
              {t("general.save")}
            </Button>
          )}
        </div>
      </Spin>
      {/** ---------------------------------------------------- */}
      {/** GLOBAL ALERT */}
      <GlobalAlert2
        isError={isErrorSave}
        isSuccess={isSuccessSave}
        requestType={isEditMode ? "PUT" : "POST"}
        error={withMissingPermissions(errorSave, t("organizationManagement.missingPermissions"))}
        name="Role"
      />
    </div>
  );
}

export default RolesCreateRoleForm;
